import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { useSelector } from "react-redux";

import {
  TableContainer,
  TableHead,
  Table,
  TableBody,
  TableRow,
} from "@mui/material";

import { ClearLines } from "components/EditPage/ClearLines";
import { LineTableHeadingCell } from "components/formFields/LineTableCell";
import { LineColumnFillHeading } from "components/formFields/line/LineColumnFillHeading";
import { LineColumnHeading } from "components/formFields/line/LineColumnHeading";
import { LineCostAdjustmentHeading } from "components/formFields/line/LineCostAdjustment";
import { LineInventoryItemHeading } from "components/formFields/line/LineInventoryItem";
import { LineItemFooter } from "components/formFields/line/LineItemFooter";
import { DragHandleHeading } from "components/utility/DragHandle";
import { EditLineButtonHeading } from "components/utility/EditLineButton";

import { i18n } from "services/i18nService";
import { expandItemGroup } from "services/sosInventoryService/expandItemGroup";

import { useClasses } from "hooks/useClasses.js";
import { useJobWorkcenters } from "hooks/useJobWorkcenters";
import { useLineItemHandlers } from "hooks/useLineItemHandlers";

import { MemoizedLineItem } from "views/Adjustments/Adjustment/MemoizedLineItem";

import { EMPTY_LINE_ITEM } from "editConfig";

export function LineItems(props) {
  const {
    record,
    objectType,
    lines,
    items,
    totals,
    location,
    itemsCount,
    addItem,
    lineHandler,
    setClearLines,
    lineItemsErrors,
  } = props;

  const { classes, classesCount } = useClasses();
  const jobWorkcenters = useJobWorkcenters();

  const {
    readOnlyLineIds,
    setLineEdit,
    handleDeleteLine,
    insertEmptyLine,
    handleDrop,
  } = useLineItemHandlers(
    record?.id,
    lines,
    lineHandler,
    EMPTY_LINE_ITEM[objectType]
  );

  // SETTINGS
  const classEnabled = useSelector(
    (state) => state.userCompanySettings.settings.classEnabled
  );
  const jobEnabled = useSelector(
    (state) => state.userCompanySettings.settings.jobEnabled
  );
  const uomEnabled = useSelector(
    (state) => state.userCompanySettings.settings.uomEnabled
  );

  if (!lines) {
    return null;
  }

  return (
    <>
      <TableContainer data-testing="lineItems" sx={{ overflowX: "initial" }}>
        <Table size="small" padding="none" stickyHeader>
          <TableHead>
            <TableRow sx={{ verticalAlign: "bottom", lineHeight: "1" }}>
              <DragHandleHeading />
              <EditLineButtonHeading recordId={record.id} />
              <ClearLines setClearLines={setClearLines} />
              <LineTableHeadingCell>
                {i18n("columns.Line")}
              </LineTableHeadingCell>
              <LineInventoryItemHeading lineHandler={lineHandler} />
              <LineColumnHeading labelCode="Description" />
              <LineColumnHeading labelCode="OldQuantity" align="right" />
              <LineColumnHeading labelCode="NewQuantity" />
              <LineColumnHeading labelCode="AdjustQuantityBy" />
              {uomEnabled && <LineColumnHeading labelCode="UOM" />}
              <LineCostAdjustmentHeading
                lines={lines}
                date={record.date}
                lineHandler={lineHandler}
              />
              {classEnabled && (
                <LineColumnFillHeading
                  labelCode="Class"
                  field="class"
                  lineHandler={lineHandler}
                />
              )}
              {jobEnabled && (
                <LineColumnFillHeading
                  labelCode="Job"
                  field="jobWorkcenter"
                  lineHandler={lineHandler}
                />
              )}
            </TableRow>
          </TableHead>
          <DragDropContext onDragEnd={handleDrop}>
            <Droppable droppableId="lineItems">
              {(provided) => (
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {lines.map((line, index) => {
                    const lineReadOnly = readOnlyLineIds.has(index);

                    return (
                      <MemoizedLineItem
                        key={line.id || `temp-${index}`}
                        index={index}
                        objectType={objectType}
                        record={record}
                        location={location}
                        line={line}
                        lineHandler={lineHandler}
                        expandItemGroup={(lineToExpand) =>
                          expandItemGroup(
                            lineToExpand,
                            lineHandler,
                            objectType,
                            record
                          )
                        }
                        items={items}
                        itemsCount={itemsCount}
                        classes={classes}
                        classesCount={classesCount}
                        addItem={addItem}
                        jobWorkcenters={jobWorkcenters}
                        lineItemsErrors={lineItemsErrors}
                        insertEmptyLine={insertEmptyLine}
                        isLastLine={line.lineNumber === lines.length}
                        setLineEdit={setLineEdit}
                        lineReadOnly={lineReadOnly}
                        nextLineReadOnly={readOnlyLineIds.has(index + 1)}
                        handleDeleteLine={handleDeleteLine}
                      />
                    );
                  })}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </DragDropContext>
          <LineItemFooter
            firstCellSpan={6}
            totals={totals}
            quantityString="quantityDiff"
            lineHandler={lineHandler}
            objectType={objectType}
            recordId={record.id}
          />
        </Table>
      </TableContainer>
    </>
  );
}
